import { BigNumber, ethers } from "ethers";
import Web3Modal from "web3modal";
import { Subject, BehaviorSubject } from "rxjs";
import {
  LABS_ROYALTIES_ADDRESS,
  LABS_ROYALTIES_ABI,
  DRDAO_ROYALTIES_ADDRESS,
  DRDAOS_ROYALTIES_ABI,
  TOKENS_WALLET_ADDRESS,
  TOKENS_WALLET_ABI,
  AIEXP_ROYALTIES_ADDRESS,
  AIEXP_ROYALTIES_ABI,
  MUTANTS_ADDRESS,
  MUTANTS_ABI,
  MUTANTS_ROYALTIES_ADDRESS,
  MUTANTS_ROYALTIES_ABI,
  ULTIMATE_ADDRESS,
  ULTIMATE_ABI,
  DAOPASS_ADDRESS,
  ERC721_STAKING_ADRESS,
  ERC721_STAKING_CONTRACT_ABI,
  BOO_STAKING_ADDRESS,
  BOO_STAKING_ABI,
  BOO_CONTRACT_ADDRESS,
  BOO_CONTRACT_ABI,
  GHOST_OG_STAKING_ADDRESS,
} from ".././config";
import { MintInfo } from "./MintInfo";
import { RoyaltiesInfo } from "./RoyaltiesInfo";
import { ChainModel } from "./ChainModel";

import WalletConnectProvider from "@walletconnect/web3-provider";
import { TokenInfo } from "./TokenInfo";
import Web3 from "web3";
import Collections from "./Collections";

export enum UltimateMutantType {
  CYBORGS,
  ALIENS,
  GHOSTS,
  ZOMBIES,
}

export default class Web3Service {
  static instance: Web3Service;

  static shared() {
    if (Web3Service.instance) {
      return Web3Service.instance;
    } else {
      Web3Service.instance = new Web3Service();
      return Web3Service.instance;
    }
  }

  static cronosRpc = "https://mmf-rpc.xstaking.sg";
  static defaultProvider = new ethers.providers.JsonRpcProvider(
    Web3Service.cronosRpc,
  );

  // Private
  private _connected$ = new BehaviorSubject<boolean>(false);
  private _isLoading$ = new BehaviorSubject<boolean>(false);
  private _account$ = new BehaviorSubject<string | undefined>(undefined);
  private _mintInfo$ = new BehaviorSubject<MintInfo | undefined>(undefined);
  private _royalties$ = new BehaviorSubject<RoyaltiesInfo | undefined>(
    undefined,
  );
  private _royaltiesDao$ = new BehaviorSubject<RoyaltiesInfo | undefined>(
    undefined,
  );
  private _royaltiesExp$ = new BehaviorSubject<RoyaltiesInfo | undefined>(
    undefined,
  );
  private _royaltiesMutants$ = new BehaviorSubject<RoyaltiesInfo | undefined>(
    undefined,
  );
  private _royaltiesUltimate$ = new BehaviorSubject<RoyaltiesInfo | undefined>(
    undefined,
  );
  private _tokens$ = new BehaviorSubject<number[]>([]);
  private _signature$ = new BehaviorSubject<string | undefined>(undefined);
  private _showToast$ = new Subject<{ title: string }>();
  private _errors$ = new Subject<string>();
  private _isApproved$ = new BehaviorSubject<boolean>(false);
  private _hasPass$ = new BehaviorSubject<boolean>(false);
  private _stakedNfts$ = new BehaviorSubject<number[]>([]);
  private _stakedTokens$ = new BehaviorSubject<BigNumber>(BigNumber.from(0));
  private _stakedTokenRate$ = new BehaviorSubject<number | undefined>(0);
  private _rewards$ = new BehaviorSubject<number | undefined>(0);
  private _rewardDuration$ = new BehaviorSubject<number | undefined>(0);
  private _booTokens$ = new BehaviorSubject<BigNumber>(BigNumber.from(0));
  private _booReward$ = new BehaviorSubject<number>(0);
  private _booRewardRate$ = new BehaviorSubject<number>(0);
  private _booRewardDuration$ = new BehaviorSubject<number>(0);

  // Public
  public readonly connected$ = this._connected$.asObservable();
  public readonly account$ = this._account$.asObservable();
  public readonly mintInfo$ = this._mintInfo$.asObservable();
  public readonly royalties$ = this._royalties$.asObservable();
  public readonly royaltiesDao$ = this._royaltiesDao$.asObservable();
  public readonly royaltiesExp$ = this._royaltiesExp$.asObservable();
  public readonly royaltiesMutants$ = this._royaltiesMutants$.asObservable();
  public readonly royaltiesUltimate$ = this._royaltiesUltimate$.asObservable();
  public readonly tokens$ = this._tokens$.asObservable();
  public readonly signature$ = this._signature$.asObservable();
  public readonly showToast$ = this._showToast$.asObservable();
  public readonly errors$ = this._errors$.asObservable();
  public readonly isLoading$ = this._isLoading$.asObservable();
  public readonly isApproved$ = this._isApproved$.asObservable();
  public readonly hasPass$ = this._hasPass$.asObservable();
  public readonly stakedNfts$ = this._stakedNfts$.asObservable();
  public readonly stakedTokens$ = this._stakedTokens$.asObservable();
  public readonly stakedTokenRate$ = this._stakedTokenRate$.asObservable();
  public readonly rewards$ = this._rewards$.asObservable();
  public readonly rewardDuration$ = this._rewardDuration$.asObservable();
  public readonly booTokens$ = this._booTokens$.asObservable();
  public readonly booReward$ = this._booReward$.asObservable();
  public readonly booRewardRate$ = this._booRewardRate$.asObservable();
  public readonly booRewardDuration$ = this._booRewardDuration$.asObservable();

  // Logic
  private web3Modal: Web3Modal;
  private provider?: ethers.providers.Web3Provider;
  private didConnectOnLoad: boolean = false;
  private connector: any;

  private _chain = new ChainModel(25, "Cronos", "Crypto.org Coin", 18, "CRO", [
    "https://evm.cronos.org",
  ]);

  constructor() {
    const providerOptions = {
      "custom-walletconnect": {
        display: {
          logo: "https://docs.walletconnect.com/img/walletconnect-logo.svg",
          name: "WalletConnect",
          description: "Connect with any WalletConnect compatible wallet.",
        },
        options: {
          appName: "Moon Crew Apes",
          networkUrl: "https://evm-cronos.crypto.org/",
          chainId: 25,
        },
        package: WalletConnectProvider,
        connector: async () => {
          const connector = new WalletConnectProvider({
            rpc: {
              25: "https://evm-cronos.crypto.org",
            },
            chainId: 25,
          });
          await connector.enable();
          this.connector = connector;
          return connector;
        },
      },
    };

    this.web3Modal = new Web3Modal({
      cacheProvider: true,
      providerOptions,
    });
  }

  isCorrectChainId = () => {
    if (window.ethereum)
      return window.ethereum.networkVersion == this._chain.id;
    return true;
  };

  connectToCachedProvider = async () => {
    if (this.didConnectOnLoad || !this.web3Modal.cachedProvider) return;

    this._isLoading$.next(true);
    this.didConnectOnLoad = true;
    this.web3Modal
      .connectTo(this.web3Modal.cachedProvider)
      .then((provider) => {
        this.walletConnected(provider);
        this._isLoading$.next(false);
      })
      .catch((error) => {
        this._errors$.next("Failed to connect");
        this._isLoading$.next(false);
      });
  };

  toggleConnect = async () => {
    if (this._connected$.value) {
      try {
        this.connector.disconnect();
      } catch (error) {}
      try {
        this.connector.deactivate();
      } catch (error) {}

      this.disconnect();
    } else {
      this.connectToWallet();
    }
  };

  switchNetwork = async () => {
    if (this.isCorrectChainId()) return;

    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: ethers.utils.hexValue(this._chain.id) }],
      });
    } catch (err: any) {
      if (err.code == 4902) {
        await window.ethereum.request({
          method: "wallet_addEthereumChain",
          params: [
            {
              chainName: this._chain.name,
              chainId: ethers.utils.hexValue(this._chain.id),
              nativeCurrency: {
                name: this._chain.currencyName,
                decimals: this._chain.decimals,
                symbol: this._chain.symbol,
              },
              rpcUrls: this._chain.rpcUrls,
            },
          ],
        });
      } else if (err.code == 4901) {
        return;
      } else {
        this._errors$.next(
          "There was a problem adding " +
            this._chain.name +
            " network to MetaMask",
        );
      }
    }
  };

  private walletConnected(provider: any) {
    this.provider = new ethers.providers.Web3Provider(provider);
    this._connected$.next(true);
    this._showToast$.next({ title: "Wallet connected" });
    this.getAccount();

    this.observeWalletChanges();
  }

  private observeWalletChanges() {
    this.removeListeners();

    const provider = new ethers.providers.Web3Provider(window.ethereum, "any");

    provider.on("network", (newNetwork, oldNetwork) => {
      if (oldNetwork) {
        window.location.reload();
      }
    });

    window.ethereum.on("accountsChanged", (accounts: string[]) => {
      if (accounts[0] && accounts[0] != this._account$.value) {
        this.getAccount();
      }
    });
  }

  private removeListeners() {
    const provider = new ethers.providers.Web3Provider(window.ethereum, "any");
    provider.removeAllListeners();
  }

  private connectToWallet = async () => {
    this.web3Modal.clearCachedProvider();

    this._isLoading$.next(true);
    this.web3Modal
      .connect()
      .then((provider) => {
        this._isLoading$.next(false);
        this.didConnectOnLoad = true;
        this.walletConnected(provider);
      })
      .catch((error) => {
        this._isLoading$.next(false);
        this._errors$.next("Failed to connect");
      });
  };

  private disconnect = async () => {
    this.web3Modal.clearCachedProvider();
    this._connected$.next(false);
    this._account$.next(undefined);
    this._mintInfo$.next(undefined);
  };

  private getAccount = async () => {
    if (!this.provider) return;

    this._isLoading$.next(true);
    const signer = this.provider.getSigner();
    const address = (await signer.getAddress()).toLowerCase();
    // 0xD1BE7Ff7f9a4CD311c277A8E0D8E57f1634e89D7
    this._account$.next(address);
    this._isLoading$.next(false);
  };

  // MINTING
  mint = async (
    tokensToBurn: number[],
    type: UltimateMutantType,
    increaseGas: boolean,
  ) => {
    if (!this.provider || !this._account$.value) {
      this.connectToWallet();
      return;
    }

    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      ULTIMATE_ADDRESS,
      ULTIMATE_ABI,
      signer,
    );

    this._isLoading$.next(true);
    try {
      const price = await contract.price();

      switch (type) {
        case UltimateMutantType.CYBORGS:
          if (increaseGas) {
            const gasEstimated = await contract.estimateGas.mintCyborgs(
              tokensToBurn,
              { value: price },
            );
            const gas = Math.ceil(gasEstimated.toNumber() * 2);
            const gasNumber = BigNumber.from(gas);

            const tx = await contract.mintCyborgs(tokensToBurn, {
              value: price,
              gasLimit: gasNumber,
            });
            await tx.wait();
          } else {
            const tx = await contract.mintCyborgs(tokensToBurn, {
              value: price,
            });
            await tx.wait();
          }
          break;
        case UltimateMutantType.ALIENS:
          if (increaseGas) {
            const gasEstimated = await contract.estimateGas.mintAliens(
              tokensToBurn,
              { value: price },
            );
            const gas = Math.ceil(gasEstimated.toNumber() * 2);
            const gasNumber = BigNumber.from(gas);

            const tx = await contract.mintAliens(tokensToBurn, {
              value: price,
              gasLimit: gasNumber,
            });
            await tx.wait();
          } else {
            const tx = await contract.mintAliens(tokensToBurn, {
              value: price,
            });
            await tx.wait();
          }
          break;
        case UltimateMutantType.GHOSTS:
          if (increaseGas) {
            const gasEstimated = await contract.estimateGas.mintGhosts(
              tokensToBurn,
              { value: price },
            );
            const gas = Math.ceil(gasEstimated.toNumber() * 2);
            const gasNumber = BigNumber.from(gas);

            const tx = await contract.mintGhosts(tokensToBurn, {
              value: price,
              gasLimit: gasNumber,
            });
            await tx.wait();
          } else {
            const tx = await contract.mintGhosts(tokensToBurn, {
              value: price,
            });
            await tx.wait();
          }
          break;
        case UltimateMutantType.ZOMBIES:
          if (increaseGas) {
            const gasEstimated = await contract.estimateGas.mintZombies(
              tokensToBurn,
              { value: price },
            );
            const gas = Math.ceil(gasEstimated.toNumber() * 2);
            const gasNumber = BigNumber.from(gas);

            const tx = await contract.mintZombies(tokensToBurn, {
              value: price,
              gasLimit: gasNumber,
            });
            await tx.wait();
          } else {
            const tx = await contract.mintZombies(tokensToBurn, {
              value: price,
            });
            await tx.wait();
          }
          break;
      }

      this._showToast$.next({ title: " Ultimate Mutant has been minted" });

      await this.getUserTokens(MUTANTS_ADDRESS);
    } catch (e) {
      console.log(e);
      this._errors$.next("Could not process transaction");
    } finally {
      this._isLoading$.next(false);
    }
  };

  getMintInfo = async () => {
    if (!this.provider || !this._account$.value) return;
    const contract = new ethers.Contract(
      ULTIMATE_ADDRESS,
      ULTIMATE_ABI,
      this.provider,
    );

    try {
      const mintInfo = await contract.getMintInfo();
      this._mintInfo$.next(mintInfo);
    } catch (e) {
      console.log(e);
    }
  };

  approveForAll = async () => {
    if (!this.provider || !this._account$.value) return;
    const contract = new ethers.Contract(
      MUTANTS_ADDRESS,
      MUTANTS_ABI,
      this.provider.getSigner(),
    );

    this._isLoading$.next(true);

    try {
      const tx = await contract.setApprovalForAll(ULTIMATE_ADDRESS, true);
      await tx.wait();
      await this.isApproved();
    } catch (e) {
      this._errors$.next("Failed to approve");
    } finally {
      this._isLoading$.next(false);
    }
  };

  isApproved = async () => {
    if (!this.provider || !this._account$.value) return;

    const erc721 = new ethers.Contract(
      MUTANTS_ADDRESS,
      MUTANTS_ABI,
      this.provider ?? Web3Service.defaultProvider,
    );

    try {
      const isApproved = await erc721.isApprovedForAll(
        this._account$.value,
        ULTIMATE_ADDRESS,
      );
      this._isApproved$.next(isApproved);
    } catch (e) {
      this._isApproved$.next(false);
    }

    const erc721Pass = new ethers.Contract(
      DAOPASS_ADDRESS,
      MUTANTS_ABI,
      this.provider ?? Web3Service.defaultProvider,
    );
    try {
      const balance = await erc721Pass.balanceOf(this._account$.value);
      this._hasPass$.next(Number(balance) > 0);
    } catch (e) {
      this._hasPass$.next(false);
    }
  };

  // ROYALTIES
  getLabsRoyalties = async () => {
    if (!this.provider || !this._account$.value) return;

    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      LABS_ROYALTIES_ADDRESS,
      LABS_ROYALTIES_ABI,
      signer,
    );

    try {
      const availableToClaim = Number(
        ethers.utils.formatEther(
          await contract.getRoyalties(this._account$.value),
        ),
      ).toFixed(2);
      const totalRoyalties = Number(
        ethers.utils.formatEther(await contract.totalRoyalties()),
      );
      this._royalties$.next({ totalRoyalties, availableToClaim });
    } catch (e) {
      //
    }
  };

  claimAllLabsRoyalties = async () => {
    if (!this.provider || !this._account$.value) return;

    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      LABS_ROYALTIES_ADDRESS,
      LABS_ROYALTIES_ABI,
      signer,
    );

    try {
      const tokens: TokenInfo[] = await contract.getRoyaltiesDetails(
        this._account$.value,
      );

      const ids = tokens
        .filter(
          (token: TokenInfo) =>
            Number(ethers.utils.formatEther(token.royalties)) > 0,
        )
        .map((token: TokenInfo) => Number(token.id));

      const tx = await contract.claimRoyalties(ids);
      await tx.wait();

      this._showToast$.next({ title: "Royalties have been claimed" });
    } catch (e) {
      this._errors$.next("Could not claim royalties");
    }
  };

  claimAllExpRoyalties = async () => {
    if (!this.provider || !this._account$.value) return;

    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      AIEXP_ROYALTIES_ADDRESS,
      AIEXP_ROYALTIES_ABI,
      signer,
    );

    try {
      const tx = await contract.claimAllRoyalties();
      await tx.wait();

      this._showToast$.next({ title: "Royalties have been claimed" });
    } catch (e) {
      console.error(e);
      this._errors$.next("Could not claim royalties");
    }
  };

  getMutantsRoyalties = async () => {
    if (!this.provider || !this._account$.value) return;

    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      MUTANTS_ROYALTIES_ADDRESS,
      MUTANTS_ROYALTIES_ABI,
      signer,
    );

    try {
      const availableToClaim = Number(
        ethers.utils.formatEther(
          await contract.getRoyalties(this._account$.value),
        ),
      ).toFixed(2);
      const totalRoyalties = Number(
        ethers.utils.formatEther(await contract.totalRoyalties()),
      );
      this._royaltiesMutants$.next({ totalRoyalties, availableToClaim });
    } catch (e) {
      console.error(e);
    }
  };

  claimAllMutantsRoyalties = async () => {
    if (!this.provider || !this._account$.value) return;

    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      MUTANTS_ROYALTIES_ADDRESS,
      MUTANTS_ROYALTIES_ABI,
      signer,
    );

    try {
      const tx = await contract.claimAllRoyalties();
      await tx.wait();

      this._showToast$.next({ title: "Royalties have been claimed" });
    } catch (e) {
      console.error(e);
      this._errors$.next("Could not claim royalties");
    }
  };

  getDrDaoRoyalties = async () => {
    if (!this.provider || !this._account$.value) return;

    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      DRDAO_ROYALTIES_ADDRESS,
      DRDAOS_ROYALTIES_ABI,
      signer,
    );

    try {
      const availableToClaim = Number(
        ethers.utils.formatEther(
          await contract.getRoyalties(this._account$.value),
        ),
      ).toFixed(2);
      const totalRoyalties = Number(
        ethers.utils.formatEther(await contract.totalRoyalties()),
      );
      this._royaltiesDao$.next({ totalRoyalties, availableToClaim });
    } catch (e) {
      //
    }
  };

  getExpRoyalties = async () => {
    if (!this.provider || !this._account$.value) return;

    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      AIEXP_ROYALTIES_ADDRESS,
      AIEXP_ROYALTIES_ABI,
      signer,
    );

    try {
      const availableToClaim = Number(
        ethers.utils.formatEther(
          await contract.getRoyalties(this._account$.value),
        ),
      ).toFixed(2);
      const totalRoyalties = Number(
        ethers.utils.formatEther(await contract.totalRoyalties()),
      );
      this._royaltiesExp$.next({ totalRoyalties, availableToClaim });
    } catch (e) {
      console.error(e);
    }
  };

  claimAllDrDaoRoyalties = async () => {
    if (!this.provider || !this._account$.value) return;

    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      DRDAO_ROYALTIES_ADDRESS,
      DRDAOS_ROYALTIES_ABI,
      signer,
    );

    try {
      const tokens: TokenInfo[] = await contract.getRoyaltiesDetails(
        this._account$.value,
      );

      const ids = tokens
        .filter(
          (token: TokenInfo) =>
            Number(ethers.utils.formatEther(token.royalties)) > 0,
        )
        .map((token: TokenInfo) => Number(token.id));

      const tx = await contract.claimRoyalties(ids);
      await tx.wait();

      this._showToast$.next({ title: "Royalties have been claimed" });
    } catch (e) {
      this._errors$.next("Could not claim royalties");
    }
  };

  getUltimateRoyalties = async () => {
    if (!this.provider || !this._account$.value) return;

    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      ULTIMATE_ADDRESS,
      ULTIMATE_ABI,
      signer,
    );

    try {
      const availableToClaim = Number(
        ethers.utils.formatEther(
          await contract.getRoyalties(this._account$.value),
        ),
      ).toFixed(2);
      const totalRoyalties = Number(
        ethers.utils.formatEther(await contract.totalRoyalties()),
      );
      this._royaltiesUltimate$.next({ totalRoyalties, availableToClaim });
    } catch (e) {
      console.error(e);
    }
  };

  claimAllUltimateRoyalties = async () => {
    if (!this.provider || !this._account$.value) return;

    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      ULTIMATE_ADDRESS,
      ULTIMATE_ABI,
      signer,
    );

    try {
      const tokens: TokenInfo[] = await contract.getRoyaltiesDetails(
        this._account$.value,
      );

      const ids = tokens
        .filter(
          (token: TokenInfo) =>
            Number(ethers.utils.formatEther(token.royalties)) > 0,
        )
        .map((token: TokenInfo) => Number(token.id));

      const tx = await contract.claimRoyalties(ids);
      await tx.wait();

      this._showToast$.next({ title: "Royalties have been claimed" });
    } catch (e) {
      this._errors$.next("Could not claim royalties");
    }
  };

  getUserTokens = async (address: string) => {
    if (!this.provider || !this._account$.value) return;

    const contract = new ethers.Contract(
      TOKENS_WALLET_ADDRESS,
      TOKENS_WALLET_ABI,
      this.provider,
    );

    try {
      const tokens = await contract.tokensOfWallet(
        this._account$.value,
        address,
      );
      this._tokens$.next(tokens);
    } catch (e) {
      //
    }
  };

  //ERC721 STAKING
  getStakedNfts = async (collectionAddress: string, isOG?: boolean) => {
    if (!this.provider || !this._account$.value) return;

    const contract = new ethers.Contract(
      !isOG ? ERC721_STAKING_ADRESS : GHOST_OG_STAKING_ADDRESS,
      ERC721_STAKING_CONTRACT_ABI,
      this.provider,
    );

    try {
      const rawNFTs: number[] = await contract.getUserStakedNFTsForCollection(
        collectionAddress,
        this._account$.value,
      );

      const stakedNfts = rawNFTs.map((e: number) => Number(e));

      this._stakedNfts$.next(stakedNfts);
    } catch (err) {}
  };

  stakeAll = async (
    collectionAddress: string,
    isOG?: boolean,
    tokens?: number[],
  ) => {
    if (!this.provider || !this._account$.value) return;
    if (this._tokens$.value.length === 0) {
      this._errors$.next("No owned NFTs");
      return;
    }

    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      !isOG ? ERC721_STAKING_ADRESS : GHOST_OG_STAKING_ADDRESS,
      ERC721_STAKING_CONTRACT_ABI,
      signer,
    );

    try {
      await this.approveToken(collectionAddress, isOG);

      const rawStakePrice = await contract.chargeStake();
      const stakePrice = Number(rawStakePrice) * tokens?.length!;
      const tx = await contract.stake(collectionAddress, tokens, {
        value: ethers.utils.parseEther(stakePrice.toString()),
      });
      await tx.wait();
      this.getUserTokens(collectionAddress);
      this.getStakedNfts(collectionAddress, isOG);
      this.getUserReward(collectionAddress, isOG);
      this._showToast$.next({ title: "All nfts successfully staked!" });
    } catch (err) {
      this._errors$.next("Could not stake NFTs");
    }
  };

  approveToken = async (collectionAddress: string, isOG?: boolean) => {
    const abi = Collections.filter(
      (e) => e.collectionAddress === collectionAddress,
    )[0].abi;

    const signer = this.provider?.getSigner();
    const contract = new ethers.Contract(collectionAddress, abi, signer);
    try {
      const isApproved = await contract.isApprovedForAll(
        this._account$.value,
        !isOG ? ERC721_STAKING_ADRESS : GHOST_OG_STAKING_ADDRESS,
      );
      if (!isApproved) {
        const tx = await contract.setApprovalForAll(
          !isOG ? ERC721_STAKING_ADRESS : GHOST_OG_STAKING_ADDRESS,
          true,
        );
        await tx.wait();
      }
    } catch (err) {
      console.error(err);
    }
  };

  withdrawAll = async (collectionAddress: string, isOG?: boolean) => {
    if (!this.provider || !this._account$.value) return;
    if (this._stakedNfts$.value.length === 0) {
      this._errors$.next("No staked NFTs");
      return;
    }

    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      !isOG ? ERC721_STAKING_ADRESS : GHOST_OG_STAKING_ADDRESS,
      ERC721_STAKING_CONTRACT_ABI,
      signer,
    );

    try {
      const stakedNFTs: number[] =
        await contract.getUserStakedNFTsForCollection(
          collectionAddress,
          this._account$.value,
        );
      const rawWithdrawPrice: BigNumber = await contract.chargeWithdraw();

      const withdrawPrice: number =
        Number(rawWithdrawPrice) * stakedNFTs.length;

      const tx = await contract.withdraw(collectionAddress, stakedNFTs, {
        value: ethers.utils.parseEther(withdrawPrice.toString()),
      });
      await tx.wait();

      this.getUserTokens(collectionAddress);
      this.getStakedNfts(collectionAddress, isOG);
      this.getUserReward(collectionAddress, isOG);
      this._showToast$.next({ title: "All nfts withdrawn successfully" });
    } catch (err) {
      this._errors$.next("Could not withdraw NFTs");
    }
  };

  getUserReward = async (collectionAddress: string, isOG?: boolean) => {
    if (!this.provider || !this._account$.value) return;

    const contract = new ethers.Contract(
      !isOG ? ERC721_STAKING_ADRESS : GHOST_OG_STAKING_ADDRESS,
      ERC721_STAKING_CONTRACT_ABI,
      this.provider,
    );

    try {
      const rawReward = await contract.earned(
        collectionAddress,
        this._account$.value,
      );

      const reward = parseFloat((Number(rawReward) / 1e18).toFixed(3));

      this._rewards$.next(reward);
    } catch (err) {
      console.log(err);
    }
  };

  getRewardDuration = async (isOG?: boolean) => {
    if (!this.provider || !this._account$.value) return;

    const contract = new ethers.Contract(
      !isOG ? ERC721_STAKING_ADRESS : GHOST_OG_STAKING_ADDRESS,
      ERC721_STAKING_CONTRACT_ABI,
      this.provider,
    );

    try {
      const rawPeriodFinish = await contract.periodFinish();

      const periodFinish = Number(rawPeriodFinish);

      this._rewardDuration$.next(periodFinish);
    } catch (err) {
      console.log(err);
    }
  };

  getRewardRate = async (isOG?: boolean) => {
    if (!this.provider || !this._account$.value) return;

    const contract = new ethers.Contract(
      !isOG ? ERC721_STAKING_ADRESS : GHOST_OG_STAKING_ADDRESS,
      ERC721_STAKING_CONTRACT_ABI,
      this.provider,
    );

    try {
      const rawRate = await contract.rewardRate();
      const rewardRate = isOG
        ? parseFloat(Number((rawRate / 1e18) * 86400).toFixed(3))
        : ((Number(rawRate) * 86400) / (5 * 1e18)).toFixed(3);

      this._stakedTokenRate$.next(rewardRate as number);
    } catch (err) {
      console.log(err);
    }
  };

  claimRewards = async (isOG?: boolean) => {
    if (!this.provider || !this._account$.value) return;
    if (this._rewards$.value === 0) {
      this._errors$.next("Not enough rewards");
      return;
    }

    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      !isOG ? ERC721_STAKING_ADRESS : GHOST_OG_STAKING_ADDRESS,
      ERC721_STAKING_CONTRACT_ABI,
      signer,
    );

    try {
      const rawChargeClaim = await contract.chargeClaim();
      const chargeClaim = Number(rawChargeClaim);

      const tx = await contract.getReward({
        value: ethers.utils.parseEther(chargeClaim.toString()),
      });

      await tx.wait();

      this._showToast$.next({ title: "Successfully claimed reward" });
      this._rewards$.next(0);
    } catch (err) {
      this._errors$.next("Error claiming rewards");
    }
  };

  //BOO Staking
  getBooToken = async () => {
    if (!this.provider || !this._account$.value) return;

    const contract = new ethers.Contract(
      BOO_CONTRACT_ADDRESS,
      BOO_STAKING_ABI,
      this.provider,
    );

    try {
      const rawTokens: BigNumber = await contract.balanceOf(
        this._account$.value,
      );
      const tokens: BigNumber = rawTokens;

      console.log(
        "This is the tokens ",
        Web3.utils.fromWei(rawTokens.toString()),
      );

      this._booTokens$.next(tokens);
    } catch (err) {
      console.error(err);
    }
  };

  getBooTokenRate = async () => {
    if (!this.provider || !this._account$.value) return;

    const contract = new ethers.Contract(
      BOO_STAKING_ADDRESS,
      BOO_STAKING_ABI,
      this.provider,
    );

    try {
      const rawRate = await contract.rewardRate();
      const rateString = Number((rawRate / 1e18) * 86400).toString(),
        rate = parseFloat(rateString.slice(0, rateString.indexOf(".") + 2));

      this._booRewardRate$.next(rate);
    } catch (err) {
      console.error(err);
    }
  };

  getBooRewardDuration = async () => {
    if (!this.provider || !this._account$.value) return;

    const contract = new ethers.Contract(
      BOO_STAKING_ADDRESS,
      BOO_STAKING_ABI,
      this.provider,
    );

    try {
      const rawDuration = await contract.finishAt();
      const currentTime = Math.floor(Date.now() / 1000);

      if (currentTime >= Number(rawDuration)) {
        this._booRewardDuration$.next(0);
      } else {
        this._booRewardDuration$.next(
          Number(BigNumber.from(rawDuration).toString()) - currentTime,
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  getStakedBooToken = async () => {
    if (!this.provider || !this._account$.value) return;

    const contract = new ethers.Contract(
      BOO_STAKING_ADDRESS,
      BOO_STAKING_ABI,
      this.provider,
    );

    try {
      const rawBalance: BigNumber = await contract.balanceOf(
        this._account$.value,
      );
      const balance = rawBalance;
      this._stakedTokens$.next(balance);
    } catch (err) {
      console.error(err);
    }
  };

  getBooReward = async () => {
    if (!this.provider || !this._account$.value) return;

    const contract = new ethers.Contract(
      BOO_STAKING_ADDRESS,
      BOO_STAKING_ABI,
      this.provider,
    );

    try {
      const rawReward = await contract.earned(this._account$.value);
      console.log("This is the rawReward", Number(rawReward));
      const reward = parseFloat(Number(rawReward / 1e18).toFixed(3));

      this._booReward$.next(reward);
    } catch (err) {
      console.error(err);
    }
  };

  stakeBooToken = async (tokens: BigNumber) => {
    console.log(tokens);
    if (!this.provider || !this._account$.value) return;
    if (
      tokens > this._booTokens$.value ||
      this._booTokens$.value === BigNumber.from(0)
    ) {
      this._errors$.next("Not enough tokens to stake");
      return;
    }

    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      BOO_STAKING_ADDRESS,
      BOO_STAKING_ABI,
      signer,
    );

    try {
      await this.approveTokens(tokens.toString());
      const tx = await contract.stake(tokens.toString());
      await tx.wait();

      this._stakedTokens$.next(this._stakedTokens$.value.add(tokens));
      this._booTokens$.next(this._booTokens$.value.sub(tokens));
      this._showToast$.next({
        title:
          "Successfully staked " +
          Web3.utils.fromWei(tokens.toString()) +
          " tokens",
      });
    } catch (err) {
      console.error(err);
      this._errors$.next("Error staking tokens");
    }
  };

  approveTokens = async (amount: string) => {
    const signer = this.provider?.getSigner();
    const tokenContract = new ethers.Contract(
      BOO_CONTRACT_ADDRESS,
      BOO_CONTRACT_ABI,
      signer,
    );

    try {
      //Approve token transaction
      const allowTx = await tokenContract.approve(BOO_STAKING_ADDRESS, amount);
      await allowTx.wait();
    } catch (err) {
      console.error(err);
    }
  };

  withdrawBooToken = async (tokens: BigNumber) => {
    if (!this.provider || !this._account$.value) return;
    if (
      tokens > this._stakedTokens$.value ||
      this._stakedTokens$.value === BigNumber.from(0)
    ) {
      this._errors$.next("Not enough tokens to withdraw");
      return;
    }
    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      BOO_STAKING_ADDRESS,
      BOO_STAKING_ABI,
      signer,
    );
    try {
      const tx = await contract.withdraw(tokens.toString());

      await tx.wait();

      this._stakedTokens$.next(this._stakedTokens$.value.sub(tokens));
      this._booTokens$.next(this._booTokens$.value.add(tokens));

      this._showToast$.next({
        title:
          "Successfully withdrawn " +
          Web3.utils.fromWei(tokens.toString()) +
          " tokens",
      });
    } catch (err) {
      console.error(err);
      this._errors$.next("Error withdrawing tokens");
    }
  };

  claimBooReward = async () => {
    if (!this.provider || !this._account$.value) return;
    if (this._booReward$.value === 0) {
      this._showToast$.next({ title: "No rewards to claim" });
      return;
    }

    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      BOO_STAKING_ADDRESS,
      BOO_STAKING_ABI,
      signer,
    );

    try {
      const tx = await contract.getReward();
      await tx.wait();

      this.getBooReward();

      this._showToast$.next({ title: "Successfully claimed rewards" });
    } catch (err) {
      console.error(err);
      this._errors$.next("Couldn't claim rewards");
    }
  };

  setReward = async () => {
    // 2419200
    if (!this.provider || !this._account$.value) return;
    const signer = this.provider.getSigner();
    const contract = new ethers.Contract(
      ERC721_STAKING_ADRESS,
      ERC721_STAKING_CONTRACT_ABI,
      signer,
    );

    try {
      const tx = await contract.notifyRewardAmount(100);
      await tx.wait();
    } catch (err) {
      console.error(err);
    }
  };
}
